/** Styled components for the impact map's node modal (split out of ImpactPathNodeModal.jsx, #1166). */
import styled from '@emotion/styled';
import { Loader2 } from 'lucide-react';

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, ${props => props.$dim || 0.8});
  z-index: 1000;
`;

export const CenterContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  pointer-events: none;
`;

export const RightContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 40px;
  transform: translateY(-50%);
  z-index: 1001;
`;

export const CreateSplitLayout = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const ParentSide = styled.div`
  flex: 0 0 30%;
  background: rgba(91, 157, 217, 0.06);
  border-right: 1px solid rgba(91, 157, 217, 0.15);
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 16px;
  overflow-y: auto;
`;

export const ParentCard = styled.div``;

export const ArrowDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(91, 157, 217, 0.5);
  margin-top: 8px;
`;

export const InputSide = styled.div`
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
`;

export const ParentLabel = styled.span`
  display: inline-block;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(91, 157, 217, 0.8);
  background: rgba(91, 157, 217, 0.12);
  border: 1px solid rgba(91, 157, 217, 0.2);
  border-radius: 20px;
  padding: 3px 10px;
  margin-bottom: 8px;
`;

export const ParentTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

export const ParentDesc = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  line-height: 1.5;
`;

export const ModalBox = styled.div`
  background: #0a0a0a;
  border: ${props => props.$accent ? '2px solid #5B9DD9' : '1px solid #333'};
  border-radius: ${props => props.$accent ? '8px' : '4px'};
  width: ${props => props.$width || '800px'};
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: #fff;
  pointer-events: auto;
  ${props => props.$accent && `box-shadow: 0 8px 32px rgba(91, 157, 217, 0.3);`}
`;

export const Header = styled.div`
  padding: ${props => (props.$create ? '24px 32px 16px' : props.$badged ? '15px 15px 0 24px' : '15px 15px 0 0')};
  display: flex;
  justify-content: ${props => (props.$create || props.$badged ? 'space-between' : 'flex-end')};
  align-items: ${props => props.$create ? 'flex-start' : 'center'};
  ${props => props.$create && `
    background: linear-gradient(135deg, rgba(91, 157, 217, 0.1) 0%, transparent 100%);
    border-bottom: 1px solid #333;
  `}
  gap: 8px;
  flex-shrink: 0;
`;

export const HeaderTitle = styled.h2`
  font-family: 'Rational', sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #fff;
  margin: 0;
`;

export const IconBtn = styled.button`
  background: transparent;
  border: 1px solid #555;
  border-radius: 4px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #888;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #5B9DD9;
    border-color: #5B9DD9;
    color: #fff;
  }

  svg { width: 18px; height: 18px; }
`;

export const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  &:hover { color: #fff; }
  svg { width: 24px; height: 24px; }
`;

export const Body = styled.div`
  padding: 24px 32px 32px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const NodeSection = styled.div`
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 16px;
`;

export const BottomBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 32px;
  border-top: 1px solid #222;
  flex-shrink: 0;
`;

export const NodeTitle = styled.h3`
  margin: 0 0 8px;
  font-family: 'Rational', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
`;

export const NodeDescription = styled.p`
  margin: 0;
  font-family: 'Rational', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
  color: #888;
  cursor: ${props => props.$expandable ? 'pointer' : 'default'};
  &:hover { color: ${props => props.$expandable ? '#fff' : '#888'}; }
`;

export const HumanText = styled.span` color: #7AB8E8; white-space: pre-wrap; `;
export const AIText = styled.span` color: #888; white-space: pre-wrap; `;
export const TextSeparator = styled.span` color: #555; margin: 0 4px; `;

export const Spinner = styled(Loader2)` animation: spin 1s linear infinite; @keyframes spin { to { transform: rotate(360deg); } }`;

export const EditTextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  padding-bottom: 48px;
  background: #1a1a1a;
  border: 1px solid #5B9DD9;
  border-radius: 4px;
  color: #fff;
  font-family: 'Rational', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.5;
  min-height: 200px;
  resize: vertical;
  outline: none;
  &:focus { border-color: #7AB8E8; box-shadow: 0 0 0 2px rgba(91, 157, 217, 0.2); }
`;

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * Bottom action row inside the textarea.
 *
 * Workshop feedback (2026-08-12 / #358): the AI/undo cluster sat bottom-right,
 * right next to the modal's "Tallenna", and got pressed by accident. Both AI
 * and undo now anchor LEFT (flex-start), away from the right-aligned Tallenna
 * in ButtonRow. Cluster order is Undo → Redo → Enrich, matching Solutions.
 *
 * The row also paints its own background: the buttons used to float over live
 * text, which made the transparent undo button nearly invisible and let enriched
 * text run under the controls. The solid strip keeps the bottom row clean.
 */
export const FloatingActions = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 0 0 4px 4px;
  /* Fade into the textarea background so scrolling text disappears behind the
     row instead of colliding with the buttons. */
  background: linear-gradient(to bottom, rgba(26, 26, 26, 0) 0%, #1a1a1a 42%);
  pointer-events: none;
  > * { pointer-events: auto; }
`;

export const FloatingActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const CreateTextArea = styled.textarea`
  width: 100%;
  min-height: ${props => props.$compact ? '200px' : '300px'};
  /* Grows with the content (see the auto-resize effect) but stays inside the
     modal — AI enrichment can add several paragraphs at once. */
  max-height: 60vh;
  overflow-y: auto;
  padding: 16px;
  /* Clears the floating action row so enriched text never runs under the
     buttons — workshop feedback 2026-08-12. */
  padding-bottom: 64px;
  background: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-family: 'Rational', sans-serif;
  font-size: 18px;
  line-height: 1.5;
  resize: none;
  outline: none;
  transition: border-color 0.2s, min-height 0.2s ease;
  &:focus { border-color: #5B9DD9; }
  &::placeholder { color: #555; }
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: ${props => props.$justify || 'flex-end'};
  gap: ${props => props.$gap || '12px'};
  margin-top: ${props => props.$mt || '12px'};
`;

export const Btn = styled.button`
  padding: ${props => props.$pad || '12px 28px'};
  border-radius: 4px;
  font-family: 'Rational', sans-serif;
  font-size: ${props => props.$fontSize || '1rem'};
  font-weight: ${props => props.$fontWeight || '700'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.$gap || '8px'};

  background: ${props => props.$variant === 'primary' ? '#5B9DD9' :
    props.$variant === 'danger' ? 'transparent' : 'transparent'};
  color: ${props => props.$variant === 'primary' ? '#fff' :
    props.$variant === 'danger' ? '#ef4444' : '#888'};
  border: ${props => props.$variant === 'primary' ? 'none' :
    props.$variant === 'danger' ? '1px solid #ef4444' : '1px solid #555'};

  &:hover:not(:disabled) {
    background: ${props => props.$variant === 'primary' ? '#4A8CC8' :
      props.$variant === 'danger' ? '#ef4444' : '#5B9DD9'};
    border-color: ${props => props.$variant === 'primary' ? '#4A8CC8' :
      props.$variant === 'danger' ? '#ef4444' : '#5B9DD9'};
    color: #fff;
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const DeleteConfirmBox = styled.div`
  background: #1a0a0a;
  border: 1px solid #ef4444;
  border-radius: 4px;
  padding: 20px;
  margin-top: 20px;
`;

export const DeleteWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  color: #ef4444;
  svg { flex-shrink: 0; margin-top: 2px; }
`;

export const DeleteWarningText = styled.div`
  font-family: 'Rational', sans-serif;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #fff;
  strong { color: #ef4444; display: block; margin-bottom: 4px; }
`;

export const CommentBox = styled.div`
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 8px;
`;

export const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`;

export const CommentName = styled.strong`
  font-family: 'Rational', sans-serif;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
`;

export const CommentDate = styled.span`
  font-family: 'Rational', sans-serif;
  color: #888;
  font-size: 0.75rem;
  font-weight: 500;
`;

export const CommentText = styled.p`
  font-family: 'Rational', sans-serif;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
`;

export const Label = styled.label`
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  display: block;
  margin-bottom: 8px;
`;

export const Divider = styled.div`
  height: 1px;
  background: #333;
`;

export const SectionTitle = styled.h4`
  margin: 0 0 16px;
  font-family: 'Rational', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
`;

export const ValenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 24px 0;
`;
export const ValenceLabel = styled.span`
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #888;
  margin-right: 4px;
`;
export const ValenceBtn = styled.button`
  padding: 5px 12px;
  border-radius: 999px;
  border: 1.5px solid ${p => (p.$on ? p.$color : 'rgba(255,255,255,0.18)')};
  background: ${p => (p.$on ? p.$bg : 'transparent')};
  color: ${p => (p.$on ? p.$color : 'rgba(255,255,255,0.75)')};
  font: inherit;
  font-size: 0.82rem;
  font-weight: ${p => (p.$on ? 700 : 500)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover { border-color: ${p => p.$color}; }
  &:focus-visible { outline: 2px solid ${p => p.$color}; outline-offset: 2px; }
  &::before {
    content: '';
    width: 8px; height: 8px; border-radius: 50%;
    background: ${p => p.$color};
    opacity: ${p => (p.$on ? 1 : 0.5)};
  }
`;
export const ValenceSource = styled.span`
  font-size: 0.72rem;
  color: #888;
  margin-left: 4px;
`;

export const VoteGroup = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;
export const VoteBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 999px;
  border: 1.5px solid ${p => (p.$on ? '#c4b5fd' : 'rgba(255,255,255,0.18)')};
  background: ${p => (p.$on ? 'rgba(124,58,237,0.18)' : 'transparent')};
  color: ${p => (p.$on ? '#e9d5ff' : 'rgba(255,255,255,0.75)')};
  font: inherit; font-size: 0.8rem; cursor: pointer;
  &:hover { border-color: #c4b5fd; }
  &:disabled { opacity: 0.45; cursor: default; }
  &:focus-visible { outline: 2px solid #c4b5fd; outline-offset: 2px; }
`;
export const NoteForm = styled.form`
  display: flex; gap: 8px;
  input {
    flex: 1; padding: 8px 10px; border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.04); color: #eee; font: inherit; font-size: 0.9rem;
    &:focus { outline: none; border-color: #a78bfa; }
  }
  button {
    padding: 8px 12px; border-radius: 8px; border: 1px solid rgba(167,139,250,0.5);
    background: rgba(124,58,237,0.16); color: #e9d5ff; font: inherit; font-size: 0.85rem; cursor: pointer;
    &:disabled { opacity: 0.45; cursor: default; }
  }
`;
